import { createSelector } from '@reduxjs/toolkit';
import { groupBy } from 'lodash-es';

import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type { AssetsFaceBoundingBoxSchema } from '@iconik-io/iconik-api/src/services/assets';
import type { MlPersonByAssetAndVersionNullable } from '@iconik-io/iconik-api/src/services/ml';
import type { SegmentEntityWithIdNormalized } from '@iconik-io/shared-lib/src/redux/entities/normalized/segment';
import { createEntitiesIndexSelector } from '@iconik-io/shared-lib/src/redux/entities_index/selectors';

import { DEFAULT_FACE_CONFIRMATION_MODAL_VISIBILITY } from '../../constants/data';
import { createListItemsSelector } from '../../ducks/common_list/selectors';
import type { PersonsStatusFilters } from '../../ducks/panels/asset_face_recognition/types';
import type { AppState } from '../../store/types';
import {
  comparePersons,
  getStatus,
  groupDetectedPersonsCountByStatus,
} from './utils';

export type FaceBoundingBox = AssetsFaceBoundingBoxSchema & {
  personId: string;
  status: MlPersonByAssetAndVersionNullable['status'];
  imageUrl: string | null;
  name: string | null;
  versionId: string;
  hasUnconfirmedInstances: boolean;
  personVersionFaceImageUrl: string | null;
};

export const getSegments = (state: AppState) => state.entities.segments;

export const getPersonsByVersionId = createEntitiesIndexSelector(
  'asset_persons',
  'version_id',
);

const getPersonsFilteredByIds = createSelector(
  [
    (state: AppState, versionId?: string) =>
      getPersonsByVersionId(state, versionId),
    (state: AppState) => state.panelsState.assetPersons.ids,
  ],
  (persons, ids) => {
    return persons.filter((item) => item && ids.includes(item?.id));
  },
);

const itemsSelector =
  createListItemsSelector<AssetsSegmentSchemaWithIdPatched>();
/** Select person segments, grouped by person id */
export const selectSegmentsGroupedByPersonId = createSelector(
  [
    (state) =>
      itemsSelector(
        state.entities.segments,
        state.panelsState.assetPersonSegments.ids,
      ),
    (_state, versionId) => versionId,
  ],
  (segments, versionId) => {
    const segmentValues = Object.values(segments).filter(
      (item) => item.segment_type === 'PERSON' && item.version_id === versionId,
    );
    return groupBy(segmentValues, 'person_id');
  },
);

/** Select persons sorted by status, segment time, x-coordinate and name */
export const selectSortedPersons = createSelector(
  [
    (state: AppState, versionId?: string) =>
      getPersonsFilteredByIds(state, versionId),
    selectSegmentsGroupedByPersonId,
  ],
  (persons, segments) => {
    return [...persons].sort((a, b) => comparePersons(segments, a, b));
  },
);

/** Select sorted persons, filtered by *personStatusFilters*. */
export const selectFilteredPersons = createSelector(
  [
    (
      state: AppState,
      _personStatusFilters: PersonsStatusFilters,
      versionId?: string,
    ) => selectSortedPersons(state, versionId),
    (_, personStatusFilters: PersonsStatusFilters, _versionId: string) =>
      personStatusFilters,
  ],
  (persons, personStatusFilters) => {
    // Treat filters for statuses with zero count as effectively off.
    const counts = groupDetectedPersonsCountByStatus(persons);
    const effectiveFilters: PersonsStatusFilters = {
      unconfirmed:
        personStatusFilters.unconfirmed && (counts.unconfirmed ?? 0) > 0,
      new: personStatusFilters.new && (counts.new ?? 0) > 0,
      confirmed: personStatusFilters.confirmed && (counts.confirmed ?? 0) > 0,
    };

    const noStatusesToggled = Object.values(effectiveFilters).every(
      (value) => !value,
    );

    return persons.filter((item) => {
      switch (getStatus(item)) {
        case 'UNCONFIRMED':
          return effectiveFilters.unconfirmed || noStatusesToggled;
        case 'NEW':
          return effectiveFilters.new || noStatusesToggled;
        case 'USER_CONFIRMED':
        case 'SYSTEM_CONFIRMED':
          return effectiveFilters.confirmed || noStatusesToggled;
        default:
          return false;
      }
    });
  },
);

/** Select person count by status */
export const selectPersonCountByStatus = createSelector(
  [
    (state: AppState, versionId?: string) =>
      getPersonsFilteredByIds(state, versionId),
  ],
  (persons) => groupDetectedPersonsCountByStatus(persons),
);

interface FacesBBProps {
  versionId?: string;
  timestampMs: number;
  frameDurationMs: number;
}

const getSortedPersonsByVersion = (
  state: AppState,
  props: FacesBBProps,
): MlPersonByAssetAndVersionNullable[] => {
  return props.versionId ? selectSortedPersons(state, props.versionId) : [];
};

const getTimestamp = (_state: AppState, props: FacesBBProps): number => {
  return props.timestampMs;
};

const getFrameDuration = (_state: AppState, props: FacesBBProps): number => {
  return props.frameDurationMs;
};

const getVersionId = (
  _state: AppState,
  props: FacesBBProps,
): string | undefined => {
  return props.versionId;
};

export const selectFacesBoundingBoxes = createSelector(
  [
    getSegments,
    getSortedPersonsByVersion,
    getTimestamp,
    getFrameDuration,
    getVersionId,
  ],
  (
    segments,
    sortedPersons,
    timestampMs,
    frameDurationMs,
    versionId,
  ): FaceBoundingBox[] => {
    if (!versionId) {
      return [];
    }
    // Person order map for sorting later
    const orderMap = new Map(sortedPersons.map((p, i) => [p.id, i]));

    // Helpers to snap ms → frame index
    const msToFrame = (ms: number) => Math.round(ms / frameDurationMs);

    const currentFrame = msToFrame(timestampMs);
    const HALF_FRAME_MS = frameDurationMs / 2;

    // Build index by frame
    const frameIndex = new Map<
      number,
      Array<{
        segment: SegmentEntityWithIdNormalized;
        face_bounding_box: AssetsFaceBoundingBoxSchema;
      }>
    >();

    const validPersonIds = new Set<string>(sortedPersons.map((p) => p.id));

    for (const segment of Object.values(segments)) {
      // only index PERSON segments of this version
      // with bounding boxes
      if (
        segment.segment_type !== 'PERSON' ||
        segment.version_id !== versionId ||
        !segment.face_bounding_boxes ||
        segment.person_id == null ||
        !validPersonIds.has(segment.person_id)
      ) {
        continue;
      }

      for (const face_bounding_box of segment.face_bounding_boxes) {
        if (face_bounding_box.timestamp_ms == null) {
          continue;
        }
        const frame = msToFrame(face_bounding_box.timestamp_ms);
        if (!frameIndex.has(frame)) {
          frameIndex.set(frame, []);
        }
        frameIndex.get(frame)?.push({ segment, face_bounding_box });
      }
    }

    // Gather candidates from ±1 current frames
    const candidateFrames = [currentFrame - 1, currentFrame, currentFrame + 1];

    // flatMap but default to empty array if missing
    const rawHits = candidateFrames.flatMap((f) => frameIndex.get(f) ?? []);

    // Apply the ms‐based tolerance filter
    // Accounts for rounding errors for floating point fps
    const hits = rawHits.filter(
      ({ face_bounding_box }) =>
        Math.abs((face_bounding_box.timestamp_ms ?? Infinity) - timestampMs) <=
        HALF_FRAME_MS,
    );

    // Create FaceBoundingBox objects, filter and sort them
    return (
      hits
        .flatMap(({ segment, face_bounding_box }) => {
          const person = sortedPersons.find((p) => p.id === segment.person_id);
          if (!person) {
            return [];
          }

          return {
            ...face_bounding_box,
            personId: person.id,
            status: person.status,
            imageUrl: person.image_url,
            name: person.name,
            versionId: person.version_id,
            personVersionFaceImageUrl: person.person_version_face_image_url,
            hasUnconfirmedInstances: person.has_unconfirmed_instances,
          };
        })
        // restore the original sort order
        .sort((a, b) => {
          const orderA = orderMap.get(a.personId) ?? 0;
          const orderB = orderMap.get(b.personId) ?? 0;
          return orderA - orderB;
        })
    );
  },
);

const getShowConfirmationModalSetting = (state: AppState) =>
  state.settings.merged.show_persons_confirmation_modal ??
  DEFAULT_FACE_CONFIRMATION_MODAL_VISIBILITY;

const getAssetVersionIdsWithConfirmationModeShown = (state: AppState) =>
  state.panelsState.assetFaceRecognition
    .assetVersionIdsWithConfirmationModeShown;

/** Select if already shown confirmation modal for current version */
const selectHasShownConfirmationMode = createSelector(
  [getAssetVersionIdsWithConfirmationModeShown, (_, versionId) => versionId],
  (assetVersionIdsWithConfirmationModeShown, versionId) =>
    versionId
      ? assetVersionIdsWithConfirmationModeShown.includes(versionId)
      : false,
);

/** Select if the confirmation modal should be shown for *versionId*. */
export const selectShouldShowConfirmModalForVersion = createSelector(
  [
    getShowConfirmationModalSetting,
    selectPersonCountByStatus,
    selectHasShownConfirmationMode,
  ],
  (settingEnabled, countByStatus, hasConfirmationModeShown) => {
    const hasPersonsToConfirm =
      countByStatus.new > 0 || countByStatus.unconfirmed > 0;

    return settingEnabled && hasPersonsToConfirm && !hasConfirmationModeShown;
  },
);
